/**
 * State rule, then the county's (owner, 2026-09-29): "Should always check state and then check if a county has a
 * different rule in that state. For now we only do utah."
 *
 * The state layer of each rule is the Requirement in requirements.ts (or the SetupStep in setup.ts): its text and
 * citation. This file adds, per state:
 *  - who regulates each county (the local health department) — LOCAL_DEPARTMENTS;
 *  - per department and topic, a cell: a verified local difference, "no difference found" (sourced), or unknown —
 *    LOCAL_CELLS;
 *  - when each state layer was last checked, and enacted changes not yet (or just) in effect — STATE_CHECKS;
 *  - the yearly review record — stateReviews().
 *
 * Why a county can differ at all (Utah Code 26A-1-121(1)(b), effective 5/7/2025): a local board's regulations "may not
 * be more stringent than those established by federal law, state statute, or administrative rules adopted by the
 * department" except where a statute allows it, or where the regulation was "in effect on February 1, 2022; and not
 * modified or amended after February 1, 2022". Items no state law regulates are open to local rules (26A-1-121(3)(b)).
 * So a web page that disagrees with a newer statute is a stale page, not a local rule: `stale-page-conflict` keeps
 * the state text and adds "confirm with them". Sources and dates: docs/source/research/utah-county-rules.md.
 */
import type { IsoDate, PermitType } from "./model.ts";
import { REQUIREMENTS } from "./requirements.ts";
import { BUSINESS_SETUP } from "./setup.ts";
import { countyIn, packFor } from "./states/index.ts";
import type { UsState } from "./states/types.ts";

export type { UsState } from "./states/types.ts";

/** A local (county or district) health department: the regulatory authority for food in the counties it serves. */
export interface LocalDepartment {
  id: string;
  state: UsState;
  name: string;
  /** Short name for one plain line ("Salt Lake County", "Southwest Utah"). */
  short: string;
  counties: string[];
  url: string;
  /** Where its food handler permits are issued (26B-7-413(1)(c): "a permit issued by a local health department"). */
  foodHandlerUrl?: string;
  lastChecked: IsoDate;
}

/** The rules that most often differ locally. Each maps to the requirement and setup-step ids it refines. */
export type LocalTopic = "cfsm" | "food-handler" | "mehk" | "mobile" | "temp-event" | "plan-review";

export const LOCAL_TOPICS: Record<LocalTopic, { label: string; ruleIds: string[] }> = {
  cfsm: { label: "Certified food safety manager (renewal; home kitchens)", ruleIds: ["ut-cfsm", "cfsm"] },
  "food-handler": { label: "Food handler permit (deadline, validity, issuer)", ruleIds: ["ut-food-handler-permit", "food-handler-self", "food-handler-staff"] },
  mehk: { label: "Home kitchen (MEHK) permits", ruleIds: ["mehk-permit"] },
  mobile: { label: "Food truck permit and reciprocity", ruleIds: ["ut-mfb-permit", "mfb-permit"] },
  "temp-event": { label: "Temporary event permit", ruleIds: ["ut-temp-event-permit", "temp-event-permit"] },
  "plan-review": { label: "Plan review for a new place", ruleIds: ["plan-review", "mfb-plan-review"] },
};

export type OverrideKind = "stricter" | "additional" | "stale-page-conflict" | "different-procedure";

export interface LocalOverride {
  kind: OverrideKind;
  /** The state rule in a few words, for the plain line ("every 5 years"). */
  stateSays: string;
  /** What the department says, in a few words ("their page says 3"; "a manager is required for home kitchens"). */
  localSays: string;
  /** The local regulation, ordinance or official page, with section where there is one. */
  citation: string;
  url: string;
  sourceType: "regulation" | "ordinance" | "webpage" | "form";
  /** Only for these permits (e.g. a home-kitchen rule). Absent = every permit the state rule covers. */
  appliesTo?: PermitType[];
  /** Absent = UNVERIFIED: never shown, never used (docs-first). */
  verified?: { on: IsoDate; what: string };
}

export interface LocalCell {
  state: UsState;
  department: string;
  topic: LocalTopic;
  status: "override" | "no-difference" | "unknown";
  lastChecked: IsoDate;
  /** What was opened (for "no-difference") or tried (for "unknown"). */
  checked: string;
  /** For "no-difference": the page or regulation that matched the state rule. */
  source?: { citation: string; url: string };
  /** For "override": each verified local difference (one topic can have two, e.g. renewal and home kitchens). */
  overrides?: LocalOverride[];
}

/** An enacted change: not yet in effect, or just in effect (kept as a guard that the text moved with it). */
export interface PendingChange {
  what: string;
  source: string;
  effective: IsoDate;
  /** A phrase (case-insensitive) that describes the OLD rule: once `effective` has passed it must not be in the text. */
  oldText?: string;
  /** When someone read the change against this rule's text. In effect and never read = a warning. */
  reviewedOn?: IsoDate;
}

export interface StateCheck {
  lastChecked: IsoDate;
  checkedWhat: string;
  pending?: PendingChange[];
}

export interface StateReview {
  state: UsState;
  lastFullReview: IsoDate;
  /** The next January 1: the Utah general session starts in January and most bills take effect in May. */
  nextReviewDue: IsoDate;
}

// ---------------------------------------------------------------------------------------------------------------
// Data, per state pack (states/ut-local.ts …). The unscoped names are Utah's, kept for callers that predate the packs;
// new code asks per state: departmentsOf, localCellsOf, stateChecksOf.
// ---------------------------------------------------------------------------------------------------------------

export { LOCAL_DEPARTMENTS, LOCAL_CELLS, STATE_CHECKS } from "./states/ut-local.ts";
import { LOCAL_DEPARTMENTS as UT_DEPARTMENTS, LOCAL_CELLS as UT_CELLS, STATE_CHECKS as UT_CHECKS, STATE_REVIEWS as UT_REVIEWS } from "./states/ut-local.ts";
import { loadedStates, perStateFrom, stateEpoch, stateModule } from "./states/access.ts";

/** One state's local layer (states/<st>-local.ts): Utah's is in this chunk, another state's comes from its bundle. */
type LocalModule = { LOCAL_DEPARTMENTS: LocalDepartment[]; LOCAL_CELLS: LocalCell[]; STATE_CHECKS: Record<string, StateCheck>; STATE_REVIEWS: (StateReview & { watch: { what: string; source: string; by?: string }[] })[] };
const UT_LOCAL: LocalModule = { LOCAL_DEPARTMENTS: UT_DEPARTMENTS, LOCAL_CELLS: UT_CELLS, STATE_CHECKS: UT_CHECKS, STATE_REVIEWS: UT_REVIEWS };
const LOCAL = perStateFrom<LocalModule, LocalModule>("local", (m) => m, UT_LOCAL);
const DEPARTMENTS = { get: (st: UsState) => LOCAL[st].LOCAL_DEPARTMENTS };
const CELLS = { get: (st: UsState) => LOCAL[st].LOCAL_CELLS };
const CHECKS = { get: (st: UsState) => LOCAL[st].STATE_CHECKS };
type Review = LocalModule["STATE_REVIEWS"][number];
let reviewsAt = -1;
let REVIEWS: Review[] = [];
/** Every loaded state's yearly review record (all eight, once all are loaded). */
export const stateReviews = (): Review[] => {
  if (reviewsAt !== stateEpoch()) { REVIEWS = loadedStates().flatMap((st) => LOCAL[st].STATE_REVIEWS); reviewsAt = stateEpoch(); }
  return REVIEWS;
};
export const departmentsOf = (state: UsState): LocalDepartment[] => DEPARTMENTS.get(state) ?? [];
export const localCellsOf = (state: UsState): LocalCell[] => CELLS.get(state) ?? [];
export const stateChecksOf = (state: UsState): Record<string, StateCheck> => CHECKS.get(state) ?? {};
/** Every department of every supported state (for lookups by id). */
export const allDepartments = (): LocalDepartment[] => loadedStates().flatMap((st) => DEPARTMENTS.get(st));

// ---------------------------------------------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------------------------------------------

/** "Salt Lake", "salt lake county", " SALT LAKE Co. " → "salt lake". */
export const normCounty = (c: string) => c.trim().toLowerCase().replace(/\s+/g, " ").replace(/\s+(county|co\.?)$/, "").trim();

export function departmentFor(state: UsState, county: string | undefined | null, deps: LocalDepartment[] = departmentsOf(state)): LocalDepartment | undefined {
  if (!county) return undefined;
  const n = normCounty(county);
  return deps.find((d) => d.state === state && d.counties.some((c) => normCounty(c) === n));
}

/** The topic a requirement or setup-step id belongs to, if it is one that can differ locally. */
export function topicOf(ruleId: string): LocalTopic | undefined {
  return (Object.keys(LOCAL_TOPICS) as LocalTopic[]).find((t) => LOCAL_TOPICS[t].ruleIds.includes(ruleId));
}

export interface RuleLayer { text: string; citation: string; url?: string }

export interface ResolvedRule {
  id: string;
  title: string;
  /** The state rule. Never replaced by a stale page. */
  state: RuleLayer & { lastChecked?: IsoDate };
  department?: LocalDepartment;
  /** What we know locally: a verified difference, checked and the same, not checked yet, or no county given. */
  local: "differs" | "same" | "unknown" | "no-county" | "not-local";
  /** Set when local = "differs": every verified local difference that applies to this permit. */
  overrides?: LocalOverride[];
  /** Plain line when the layers differ: "Utah law: every 5 years. Salt Lake County: check with them (their page says 3)." */
  line?: string;
  /** Every citation that applies: the state one, then the local one when it differs. */
  citations: RuleLayer[];
}

/**
 * The state rule for a requirement or setup step. A pack can replace it (its `rules`), or say it doesn't apply there (null).
 * Utah's pack replaces none: its rules are the catalog's own text.
 */
function stateLayer(ruleId: string, state: UsState = "UT"): (RuleLayer & { title: string }) | undefined {
  const own = packFor(state).rules;
  if (ruleId in own) {
    const o = own[ruleId];
    if (!o) return undefined;
    const base = REQUIREMENTS.find((x) => x.id === ruleId)?.title ?? BUSINESS_SETUP.find((x) => x.id === ruleId)?.title ?? ruleId;
    return { title: o.title ?? base, text: o.text, citation: o.citation, url: o.url };
  }
  const r = REQUIREMENTS.find((x) => x.id === ruleId);
  if (r) return { title: r.title, text: r.summary, citation: r.citation, ...(r.link ? { url: r.link } : {}) };
  const s = BUSINESS_SETUP.find((x) => x.id === ruleId);
  if (s) return { title: s.title, text: s.why, citation: s.citation, url: s.link };
  return undefined;
}

/** One plain line for a verified difference. A stale page never overrules the state text: it asks to confirm. */
export function localLine(o: LocalOverride, dep: LocalDepartment, lawLabel = packFor(dep.state).copy.lawLabel): string {
  const trim = (s: string) => s.replace(/[.\s]+$/, "");
  return o.kind === "stale-page-conflict"
    ? `${lawLabel}: ${trim(o.stateSays)}. ${dep.short}: check with them (${trim(o.localSays)}).`
    : `${lawLabel}: ${trim(o.stateSays)}. ${dep.short}: ${trim(o.localSays)}.`;
}

/** An override counts only when it is verified and sourced: an unverified claim is never shown or used. */
export const isUsable = (o: LocalOverride): boolean => !!(o.verified?.on && o.verified.what && o.citation && o.url);

/** A cell's usable overrides (for this permit, when given). */
export function usableOverrides(c: LocalCell | undefined, permit?: PermitType): LocalOverride[] {
  if (!c || c.status !== "override") return [];
  return (c.overrides ?? []).filter((o) => isUsable(o) && (!permit || !o.appliesTo || o.appliesTo.includes(permit)));
}

/**
 * The rule that applies to a business: the state rule, then its local health department's verified difference.
 * `cells` and `deps` are injectable for tests.
 */
export function requirementFor(
  ruleId: string,
  where: { state: UsState; county?: string | null; permit?: PermitType },
  opts: { cells?: LocalCell[]; deps?: LocalDepartment[] } = {},
): ResolvedRule | undefined {
  const base = stateLayer(ruleId, where.state);
  if (!base) return undefined;
  const cells = opts.cells ?? localCellsOf(where.state);
  const check = stateChecksOf(where.state)[ruleId];
  const state = { text: base.text, citation: base.citation, ...(base.url ? { url: base.url } : {}), ...(check ? { lastChecked: check.lastChecked } : {}) };
  const out: ResolvedRule = { id: ruleId, title: base.title, state, local: "not-local", citations: [{ text: base.text, citation: base.citation, ...(base.url ? { url: base.url } : {}) }] };
  const topic = topicOf(ruleId);
  if (!topic) return out;
  const dep = departmentFor(where.state, where.county, opts.deps);
  if (!dep) return { ...out, local: "no-county" };
  out.department = dep;
  const cell = cells.find((c) => c.state === where.state && c.department === dep.id && c.topic === topic);
  const os = usableOverrides(cell, where.permit);
  if (os.length) {
    return { ...out, local: "differs", overrides: os, line: os.map((o) => localLine(o, dep)).join(" "), citations: [...out.citations, ...os.map((o) => ({ text: o.localSays, citation: o.citation, url: o.url }))] };
  }
  // A cell whose only overrides are for another permit (or unverified) says nothing for this one: same as the state.
  const onlyOtherPermits = cell?.status === "override" && (cell.overrides ?? []).some(isUsable);
  return { ...out, local: cell?.status === "no-difference" || onlyOtherPermits ? "same" : "unknown" };
}

/** Every verified local line for a rule, one per department that differs (for pages with no county, e.g. the site). */
export function localDifferences(ruleId: string, state: UsState = "UT", permit?: PermitType): { department: LocalDepartment; line: string; overrides: LocalOverride[] }[] {
  const topic = topicOf(ruleId);
  if (!topic) return [];
  const out: { department: LocalDepartment; line: string; overrides: LocalOverride[] }[] = [];
  for (const d of departmentsOf(state)) {
    const os = usableOverrides(localCellsOf(state).find((c) => c.department === d.id && c.topic === topic), permit);
    if (os.length) out.push({ department: d, line: os.map((o) => localLine(o, d)).join(" "), overrides: os });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Staleness gate (owner, 2026-09-29): "last checked per state should be noted in our tables so we know if we need to
// renew the research at the first of the year about oncoming changes to these policies."
// ---------------------------------------------------------------------------------------------------------------

/** The most recent January 1 on or before `today`. */
export const lastJan1 = (today: IsoDate): IsoDate => `${today.slice(0, 4)}-01-01`;
/** The first January 1 after `day`. */
export const nextJan1 = (day: IsoDate): IsoDate => `${Number(day.slice(0, 4)) + 1}-01-01`;
const monthsBefore = (today: IsoDate, months: number): IsoDate => {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.toISOString().slice(0, 10);
};

export interface StalenessReport { fails: string[]; warns: string[] }

/**
 * Fails: a state layer last checked before the most recent January 1; a pending change in effect whose old wording is
 * still in the rule text; a review record whose nextReviewDue isn't the next January 1. Warns: a county cell or
 * department older than 12 months. Pure: pass `today` (tests inject the clock).
 */
export function stalenessReport(today: IsoDate, data: {
  checks?: Record<string, StateCheck>;
  cells?: LocalCell[];
  deps?: LocalDepartment[];
  reviews?: StateReview[];
  textOf?: (ruleId: string) => string | undefined;
} = {}): StalenessReport {
  // Default: every state's checks. Utah's keep their bare rule ids (the messages read as before); other states' are prefixed.
  const checks = data.checks ?? Object.fromEntries(loadedStates().flatMap((st) => Object.entries(CHECKS.get(st)).map(([id, c]) => [st === "UT" ? id : `${st}:${id}`, c] as const)));
  const cells = data.cells ?? loadedStates().flatMap((st) => CELLS.get(st)), deps = data.deps ?? allDepartments();
  const reviews = data.reviews ?? stateReviews();
  const textOf = data.textOf ?? ((id: string) => { const l = stateLayer(id); return l ? `${l.title} ${l.text} ${l.citation}` : undefined; });
  const fails: string[] = [], warns: string[] = [];
  const jan1 = lastJan1(today), yearAgo = monthsBefore(today, 12);
  for (const [id, c] of Object.entries(checks)) {
    if (c.lastChecked < jan1) fails.push(`${id}: state layer last checked ${c.lastChecked}, before ${jan1}. Re-open its sources (utah-county-rules.md "How to renew each January").`);
    for (const p of c.pending ?? []) {
      if (p.effective <= today && p.oldText && (textOf(id) ?? "").toLowerCase().includes(p.oldText.toLowerCase()))
        fails.push(`${id}: "${p.what}" took effect ${p.effective} but the rule still says "${p.oldText}".`);
      if (p.effective <= today && !p.reviewedOn) warns.push(`${id}: "${p.what}" took effect ${p.effective} and hasn't been read against this rule.`);
    }
  }
  for (const r of reviews) {
    if (r.nextReviewDue !== nextJan1(r.lastFullReview)) fails.push(`${r.state}: nextReviewDue ${r.nextReviewDue} should be ${nextJan1(r.lastFullReview)}.`);
    if (r.lastFullReview < jan1) fails.push(`${r.state}: last full review ${r.lastFullReview}, before ${jan1}.`);
  }
  for (const c of cells) if (c.lastChecked < yearAgo) warns.push(`${c.department}/${c.topic}: last checked ${c.lastChecked}, over 12 months ago.`);
  for (const d of deps) if (d.lastChecked < yearAgo) warns.push(`${d.id}: department record last checked ${d.lastChecked}, over 12 months ago.`);
  return { fails, warns };
}

/** Metric: cells per status for a state (verified override / no difference found / unknown). */
export function localCoverage(state: UsState = "UT"): { override: number; "no-difference": number; unknown: number; total: number } {
  const out = { override: 0, "no-difference": 0, unknown: 0, total: 0 };
  for (const c of localCellsOf(state)) {
    const status = c.status === "override" && !usableOverrides(c).length ? "unknown" : c.status;
    out[status]++; out.total++;
  }
  return out;
}

/**
 * The one line a screen shows under a rule, for the business's county. Same wording on web, phone and the site.
 * - differs: the plain two-layer line, with both citations.
 * - same: we checked; their department matches the state rule.
 * - unknown: name the one office to ask (so the owner doesn't call three).
 * - none: not a rule that differs locally, or no county yet.
 */
export interface LocalNote { status: "differs" | "same" | "unknown" | "none"; line: string; department?: LocalDepartment; citations: RuleLayer[]; checkedOn?: IsoDate }

export function localNote(ruleId: string, where: { state?: UsState; county?: string | null; permit?: PermitType }): LocalNote {
  const r = requirementFor(ruleId, { state: where.state ?? "UT", county: where.county, ...(where.permit ? { permit: where.permit } : {}) });
  // A county of the state that no department we read claims (Nevada: Carson City, Douglas …): say so, and name the state food program.
  if (r && r.local === "no-county" && where.county?.trim() && countyIn(where.state ?? "UT", where.county)) {
    const p = packFor(where.state), ph = p.stateAgency.phones[0], c = countyIn(where.state ?? "UT", where.county)!;
    return { status: "unknown", line: `We couldn't confirm who inspects food in ${/ City$/.test(c) ? c : `${c} County`}. Ask the state food program: ${p.stateAgency.name}${ph ? `, ${ph.number}` : ""}.`, citations: r.citations };
  }
  if (!r || !r.department || r.local === "not-local" || r.local === "no-county") return { status: "none", line: "", citations: r?.citations ?? [] };
  const d = r.department;
  if (r.local === "differs" && r.line && r.overrides?.length) return { status: "differs", line: r.line, department: d, citations: r.citations, checkedOn: r.overrides[0]!.verified?.on };
  const cell = localCellsOf(d.state).find((c) => c.department === d.id && c.topic === topicOf(ruleId));
  if (r.local === "same") return { status: "same", line: `${d.short}: ${packFor(d.state).copy.sameAsState} (checked ${cell?.lastChecked ?? ""}).`.replace(" (checked )", ""), department: d, citations: r.citations, ...(cell ? { checkedOn: cell.lastChecked } : {}) };
  return { status: "unknown", line: `${d.short} hasn't published its rule on this. Ask them: ${d.name}.`, department: d, citations: r.citations };
}

/** Where to get a food handler permit: the business's own department page when we have it, else the state list. */
export function foodHandlerPageFor(where: { state?: UsState; county?: string | null }): { label: string; url: string; department?: LocalDepartment } | undefined {
  const d = departmentFor(where.state ?? "UT", where.county);
  return d?.foodHandlerUrl ? { label: `${d.short}: food handler permits`, url: d.foodHandlerUrl, department: d } : undefined;
}

/** Every county in a state, A to Z, from its health departments (Utah: 29). */
export const countiesOf = (state: UsState): string[] => departmentsOf(state).flatMap((d) => d.counties).sort((a, b) => a.localeCompare(b));
